import type { ComponentProps } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import dayjs from 'dayjs';
import type {
  FlexWrapProps,
  FontSizeProps,
  FontWeightProps,
  LineHeightProps,
  MaxWidthProps,
  SpaceProps,
} from 'styled-system';
import { fontWeight, letterSpacing, maxWidth, minWidth } from 'styled-system';

import { useLocalization } from '../providers/LocaleProvider';
import type { Color } from '../theme/colors/types';
import { Flex } from './Flex';
import { HeadlineH8, SubtitleST3 } from './Typography';

interface CountDownProps
  extends LineHeightProps, SpaceProps, FontSizeProps, FontWeightProps, FlexWrapProps {
  endDate: string;
  label?: Nullable<string>;
  className?: string;
  color?: string;
  disableMaxWidth?: boolean;
  timerMinWidth?: string | Array<string | null>;
  timerId?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isOver: boolean;
}

const TIMER_INTERVAL = 1000;
const TIMER_MIN_WIDTH = '110px';

// To get the difference in days-hours-minutes-seconds between today and a given date,
// considering the midnight as deadline.
const calculateTimeLeft = (endDate: string) => {
  const now = dayjs();
  const deadline = dayjs(endDate);

  if (deadline.isBefore(now)) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isOver: true };
  }

  const seconds = dayjs(deadline).diff(now, 'second');
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  return {
    days,
    hours: hours % 24,
    minutes: minutes % 60,
    seconds: seconds % 60,
    isOver: false,
  };
};

const addLeadingZero = (value: number) => (value < 10 ? `0${value}` : value);

const formatTimeLeft = ({ days, hours, minutes, seconds }) =>
  [days, hours, minutes, seconds].map(addLeadingZero).join(':');

const formatTimeKeys = (timeLeft: Nullable<TimeLeft>) => {
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = timeLeft || {};

  return { d: days.toString(), h: hours.toString(), m: minutes.toString(), s: seconds.toString() };
};

/**
 * Date string ending with colon and four digits right after
 * seems to be inexplicable for Safari and IE11
 */
const sanitizeDate = (date: string) => date.replace('+0000', '+00:00');

const countDownContainerStyles: ComponentProps<typeof Flex> = {
  flexWrap: 'wrap',
  alignItems: 'baseline',
};

const CountDownLabel = styled(SubtitleST3)<MaxWidthProps>`
  ${maxWidth};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const countDownLabelStyles: ComponentProps<typeof CountDownLabel> = {
  as: 'p',
  maxWidth: '147px',
  my: 'sp_0',
};

const Timer = styled(HeadlineH8)<any>`
  ${fontWeight};
  ${letterSpacing};
  ${minWidth};
`;

const timerStyles: ComponentProps<typeof Timer> = {
  fontWeight: 'bold',
  letterSpacing: '2px',
  pt: 'sp_2',
};

const useTimeLeft = (endDate: string) => {
  const sanitizedEndDate = useMemo(() => sanitizeDate(endDate), [endDate]);
  const [timeLeft, setTimeLeft] = useState<Nullable<TimeLeft>>(null);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft(sanitizedEndDate));

    let timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(sanitizedEndDate);
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.isOver) {
        clearInterval(timer);
        timer = null;
      }
    }, TIMER_INTERVAL);

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [endDate]);

  return timeLeft;
};

const includeTypographyOverrides = (props: Partial<CountDownProps>) => ({
  ...(props?.fontSize && { fontSize: props.fontSize }),
  ...(props?.lineHeight && { lineHeight: props.lineHeight }),
  ...(props?.fontWeight && { fontWeight: props.fontWeight }),
});

const initialPlaceholder = '00:00:00:00'; // Static placeholder to avoid CLS in SSR

const CountDown = ({
  label,
  endDate,
  timerId,
  className,
  disableMaxWidth,
  color: textColor,
  timerMinWidth = TIMER_MIN_WIDTH,
  ...styleProps
}: CountDownProps) => {
  const timeLeft = useTimeLeft(endDate);
  const { r } = useLocalization();
  const formattedTimeLeft = timeLeft && formatTimeLeft(timeLeft);

  return (
    <Flex
      {...countDownContainerStyles}
      data-testid="countdown-container"
      className={className}
      {...(styleProps?.flexWrap ? { flexWrap: styleProps.flexWrap } : {})}
    >
      {label && (
        <CountDownLabel
          {...countDownLabelStyles}
          data-testid="count-down-label"
          aria-hidden="true"
          mr="sp_8"
          color={textColor as Color}
          {...(disableMaxWidth ? { maxWidth: 'auto' } : {})}
          {...includeTypographyOverrides(styleProps)}
        >
          {label}
        </CountDownLabel>
      )}
      <time
        role="timer"
        id={timerId}
        aria-label={`${label} ${r.t('h24_count_down_timer', formatTimeKeys(timeLeft))}`}
      >
        <Timer
          {...timerStyles}
          data-testid="count-down-timer"
          color={textColor as Color}
          pt={styleProps?.pt}
          minWidth={timerMinWidth}
          {...includeTypographyOverrides(styleProps)}
        >
          {formattedTimeLeft ? formattedTimeLeft : initialPlaceholder}
        </Timer>
      </time>
    </Flex>
  );
};

export default CountDown;
